import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { GearSix, Plus, X, Rss, ArrowClockwise, Trash, Warning, Star } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { humanTime, fmtIST } from "@/lib/utils";
import SocPhase3Settings from "@/pages/SocPhase3Settings";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { SocBusinessHours } from "@/components/SocBusinessHours";

const SEVS = ["Critical", "High", "Medium", "Low"];

export default function SocSettings() {
  const { user } = useAuth();
  const [sla, setSla] = useState(null);
  const [socClients, setSocClients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [newCat, setNewCat] = useState("");
  const [saving, setSaving] = useState(false);
  const [alerts, setAlerts] = useState(null);
  const [webhook, setWebhook] = useState("");
  const [savingAlerts, setSavingAlerts] = useState(false);
  const [mailbox, setMailbox] = useState(null);
  const [pollStatus, setPollStatus] = useState(null);
  const [savingMbox, setSavingMbox] = useState(false);
  const [polling, setPolling] = useState(false);
  const [testingMbox, setTestingMbox] = useState(false);
  const [accessResult, setAccessResult] = useState(null);
  const [teams, setTeams] = useState([]);
  const [roster, setRoster] = useState([]);
  const [analysts, setAnalysts] = useState([]);
  const [memberPick, setMemberPick] = useState({});
  const [newTeam, setNewTeam] = useState("");
  const [ingest, setIngest] = useState(null);

  const loadTeams = () => api.get("/soc/teams").then(r => setTeams(r.data)).catch(() => {});
  const loadRoster = () => api.get("/soc/teams/roster").then(r => setRoster(r.data)).catch(() => {});
  const loadAnalysts = () => api.get("/soc/analysts").then(r => setAnalysts(r.data)).catch(() => {});
  const loadIngest = () => api.get("/soc/ingest/config").then(r => setIngest(r.data)).catch(() => {});

  const load = () => api.get("/soc/settings").then(r => { setSla(r.data.sla); setCategories(r.data.categories || []); }).catch(() => {});
  useEffect(() => { api.get("/client-registry?module=soc").then(r => setSocClients(r.data)).catch(() => {}); }, []);
  const loadAlerts = () => api.get("/soc/alerts/config").then(r => { setAlerts(r.data); setWebhook(""); }).catch(() => {});
  const loadMailbox = () => {
    api.get("/soc/mailbox").then(r => setMailbox(r.data)).catch(() => {});
    api.get("/soc/mail-poll").then(r => setPollStatus(r.data)).catch(() => {});
  };
  useEffect(() => { load(); loadAlerts(); loadMailbox(); loadTeams(); loadRoster(); loadAnalysts(); loadIngest(); }, []);

  const saveMailbox = async () => {
    setSavingMbox(true);
    try { const { data } = await api.put("/soc/mailbox", { inbox: mailbox.inbox, send_from: mailbox.send_from }); setMailbox(data); loadMailbox(); toast.success("SOC mailbox saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSavingMbox(false); }
  };
  const pollNow = async () => {
    setPolling(true);
    try { const { data } = await api.post("/soc/mail-poll/run"); if (data.status === "ok") toast.success(`Poll complete — ${data.created} case(s) created`); else toast.error(data.detail || "Poll failed"); loadMailbox(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Poll failed"); }
    finally { setPolling(false); }
  };
  const togglePolling = async (enabled) => {
    try { const { data } = await api.put("/soc/mail-poll", { enabled }); setPollStatus(data); toast.success(enabled ? "SOC polling resumed" : "SOC polling paused"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const saveInterval = async () => {
    try { const { data } = await api.put("/soc/mail-poll", { interval_seconds: pollStatus.interval_seconds }); setPollStatus(data); toast.success("Poll interval saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const testAccess = async () => {
    setTestingMbox(true);
    try { const { data } = await api.post("/soc/mail-poll/test"); setAccessResult(data); if (data.ok) toast.success("SOC mailbox reachable"); else toast.error(`Graph ${data.status || ""} ${data.code || ""}`.trim()); }
    catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    finally { setTestingMbox(false); }
  };

  const save = async () => {
    setSaving(true);
    try { await api.put("/soc/settings", { sla, categories }); toast.success("SOC settings saved"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const saveCategories = async (next) => {
    const prev = categories;
    setCategories(next);
    try { const { data } = await api.put("/soc/settings", { categories: next }); setCategories(data.categories || next); toast.success("Categories saved"); }
    catch (e) { setCategories(prev); toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const addCategory = () => { const v = newCat.trim(); if (!v) return; setNewCat(""); if (!categories.includes(v)) saveCategories([...categories, v]); };
  const saveAlerts = async () => {
    setSavingAlerts(true);
    try {
      const body = { teams_enabled: alerts.teams_enabled, email_enabled: alerts.email_enabled, on_critical: alerts.on_critical, on_sla_breach: alerts.on_sla_breach, on_ingest_stale: alerts.on_ingest_stale, ingest_stale_hours: alerts.ingest_stale_hours, on_unassigned: alerts.on_unassigned, unassigned_alert_minutes: alerts.unassigned_alert_minutes };
      if (webhook.trim()) body.webhook_url = webhook.trim();
      const { data } = await api.put("/soc/alerts/config", body);
      setAlerts(data); setWebhook(""); toast.success("Alert settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSavingAlerts(false); }
  };
  const testAlert = async () => {
    try { const { data } = await api.post("/soc/alerts/test"); toast.success(data.webhook_set && data.teams_enabled ? "Test alert sent to Teams" : "Enable Teams + set a webhook to receive it"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
  };
  const setTier = (sev, key, val) => setSla({ ...sla, [sev]: { ...sla[sev], [key]: parseInt(val || "0", 10) } });

  const addTeam = async () => {
    const name = newTeam.trim();
    if (!name) return;
    try { await api.post("/soc/teams", { name }); setNewTeam(""); loadTeams(); loadRoster(); toast.success("Team added"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add team"); }
  };
  const delTeam = async (tid) => { try { await api.delete(`/soc/teams/${tid}`); loadTeams(); loadRoster(); } catch { toast.error("Delete failed"); } };
  const addMember = async (tid) => {
    const uid = memberPick[tid];
    if (!uid || uid === "none") return;
    try { await api.post(`/soc/teams/${tid}/members`, { user_id: uid }); setMemberPick(m => ({ ...m, [tid]: "none" })); loadRoster(); toast.success("Analyst added to team"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add member"); }
  };
  const removeMember = async (tid, uid) => {
    try { await api.delete(`/soc/teams/${tid}/members/${uid}`); loadRoster(); }
    catch { toast.error("Could not remove member"); }
  };
  const makeLead = async (tid, uid) => {
    try { await api.post(`/soc/teams/${tid}/members/${uid}/lead`); loadRoster(); toast.success("Team lead set"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not set team lead"); }
  };
  const saveDedup = async (patch) => {
    const next = { ...ingest, ...patch };
    setIngest(next);
    try { await api.put("/soc/ingest/config", { fuzzy_dedup: next.fuzzy_dedup, dedup_window_minutes: next.dedup_window_minutes }); toast.success("Duplicate detection updated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); loadIngest(); }
  };

  if (!sla) return <div className="p-6 text-muted-foreground">Loading settings…</div>;

  return (
    <div className="p-6 space-y-5 max-w-3xl" data-testid="soc-settings-view">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-widest text-rag-red mb-1">Security Operations</div>
        <h1 className="font-display text-3xl font-black flex items-center gap-2"><GearSix size={26} /> SOC Settings</h1>
      </div>

      <Tabs defaultValue="general" data-testid="soc-settings-tabs">
        <TabsList className="rounded-sm">
          <TabsTrigger value="general" data-testid="soc-tab-general" className="rounded-sm font-mono text-xs uppercase">General</TabsTrigger>
          <TabsTrigger value="business-hours" data-testid="soc-tab-business-hours" className="rounded-sm font-mono text-xs uppercase">Business Hours</TabsTrigger>
        </TabsList>
        <TabsContent value="general" className="mt-5 space-y-5">

      {user?.role === "admin" && <MitreGateCard />}

      <Card className="p-5 border-border rounded-sm">
        <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-1">SLA Targets (minutes · business hours)</div>
        <p className="text-xs text-muted-foreground mb-4">Response and resolution windows applied per severity when a case is created. The clock runs only inside the client's business-hours profile (see the Business Hours tab).</p>
        <table className="w-full text-sm">
          <thead className="text-[10px] font-mono uppercase text-muted-foreground"><tr><th className="text-left py-2">Severity</th><th className="text-left py-2">Response (min)</th><th className="text-left py-2">Resolution (min)</th></tr></thead>
          <tbody>
            {SEVS.map(sev => (
              <tr key={sev} className="border-t border-border">
                <td className="py-2 font-medium">{sev}</td>
                <td className="py-2"><Input data-testid={`soc-sla-${sev}-response`} type="number" value={sla[sev]?.response ?? 0} onChange={e => setTier(sev, "response", e.target.value)} className="rounded-sm h-8 w-28 text-sm" /></td>
                <td className="py-2"><Input data-testid={`soc-sla-${sev}-resolution`} type="number" value={sla[sev]?.resolution ?? 0} onChange={e => setTier(sev, "resolution", e.target.value)} className="rounded-sm h-8 w-28 text-sm" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card className="p-5 border-border rounded-sm">
        <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Case Categories</div>
        <p className="text-xs text-muted-foreground mb-3">Changes save instantly and appear in the New Case form and case detail category picker.</p>
        <div className="flex flex-wrap gap-2 mb-3">
          {categories.map(c => (
            <span key={c} data-testid={`soc-cat-${c}`} className="flex items-center gap-1 text-xs font-mono px-2 py-1 border border-border rounded-sm">
              {c}<button data-testid={`soc-cat-remove-${c}`} onClick={() => saveCategories(categories.filter(x => x !== c))} className="text-muted-foreground hover:text-rag-red"><X size={12} /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <Input data-testid="soc-cat-input" value={newCat} onChange={e => setNewCat(e.target.value)} placeholder="Add category…" className="rounded-sm h-9 text-sm max-w-xs"
            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addCategory(); } }} />
          <Button data-testid="soc-cat-add" variant="outline" className="rounded-sm text-xs font-mono uppercase" onClick={addCategory}><Plus size={14} /></Button>
        </div>
      </Card>

      {mailbox && (
        <Card className="p-5 border-border rounded-sm space-y-4" data-testid="soc-mailbox-card">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Email Ingest</div>
            <p className="text-xs text-muted-foreground mt-1">SOC uses its own mailbox, separate from ITSM. Incoming security emails become SOC cases; the client is auto-derived from the sender's domain.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-mono text-muted-foreground">Polled inbox (SOC email)</label>
              <Input data-testid="soc-inbox-input" value={mailbox.inbox || ""} onChange={e => setMailbox({ ...mailbox, inbox: e.target.value })} placeholder="socone@yourdomain.com" className="rounded-sm mt-1 text-sm font-mono" />
            </div>
            <div>
              <label className="text-xs font-mono text-muted-foreground">Send-from address (outgoing SOC email)</label>
              <Input data-testid="soc-sendfrom-input" value={mailbox.send_from || ""} onChange={e => setMailbox({ ...mailbox, send_from: e.target.value })} placeholder="Defaults to the polled inbox" className="rounded-sm mt-1 text-sm font-mono" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="soc-mailbox-save" onClick={saveMailbox} disabled={savingMbox} className="rounded-sm font-mono uppercase text-xs">{savingMbox ? "Saving…" : "Save mailbox"}</Button>
            <Button data-testid="soc-mailbox-poll" onClick={pollNow} disabled={polling} variant="outline" className="rounded-sm font-mono uppercase text-xs">{polling ? "Polling…" : "Poll now"}</Button>
            <Button data-testid="soc-mailbox-test" onClick={testAccess} disabled={testingMbox} variant="outline" className="rounded-sm font-mono uppercase text-xs">{testingMbox ? "Testing…" : "Test access"}</Button>
          </div>
          {(accessResult || pollStatus?.last_error) && (() => {
            const r = accessResult || { ...pollStatus.last_error, ok: false, mailbox: pollStatus.mailbox, fromPoll: true };
            return (
              <div data-testid="soc-mailbox-access-result" className={`rounded-sm border px-3 py-2.5 text-xs font-mono space-y-1 ${r.ok ? "border-rag-green-fg/40 bg-rag-green-bg/30" : "border-rag-red-fg/40 bg-rag-red-bg/30"}`}>
                <div className="flex items-center justify-between gap-3">
                  <span className={`font-semibold uppercase tracking-widest ${r.ok ? "text-rag-green-fg" : "text-rag-red-fg"}`}>
                    {r.ok ? "Mailbox access OK" : `${r.stage ? "Move to processed folder failed" : "Mailbox access failed"}${r.status ? ` — HTTP ${r.status}` : ""}${r.code ? ` (${r.code})` : ""}`}
                  </span>
                  <span className="text-muted-foreground">{r.mailbox}{r.fromPoll ? " · last poll" : ""}</span>
                </div>
                <p className="text-foreground/90 break-words">{r.message}</p>
                {r.hint && <p className="text-muted-foreground break-words">{r.hint}</p>}
              </div>
            );
          })()}
          {pollStatus && (
            <div className={`flex items-center justify-between gap-3 rounded-sm border px-3 py-2.5 ${pollStatus.poll_enabled ? "border-rag-green-fg/40 bg-rag-green-bg/30" : "border-rag-amber-fg/50 bg-rag-amber-bg/30"}`} data-testid="soc-poll-toggle-row">
              <div>
                <div className="text-sm font-semibold">{pollStatus.poll_enabled ? "Polling active" : "Polling paused"}</div>
                <div className="text-[11px] text-muted-foreground">
                  {pollStatus.poll_enabled
                    ? `The SOC inbox is checked every ${Math.round(pollStatus.interval_seconds / 60)} min and new emails become cases.${pollStatus.last_poll_at ? ` Last poll ${fmtIST(pollStatus.last_poll_at)}.` : ""}`
                    : "Inbound SOC emails are NOT being ingested automatically — only \"Poll now\" pulls them."}
                </div>
              </div>
              <Button data-testid="soc-poll-toggle-btn" onClick={() => togglePolling(!pollStatus.poll_enabled)} variant="outline"
                className={`rounded-sm font-mono text-xs uppercase tracking-widest shrink-0 ${pollStatus.poll_enabled ? "border-rag-amber-fg/50 text-rag-amber-fg" : "border-rag-green-fg/50 text-rag-green-fg"}`}>
                {pollStatus.poll_enabled ? "Pause polling" : "Resume polling"}
              </Button>
            </div>
          )}
          {pollStatus && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2 border-t border-border" data-testid="soc-poll-status">
              <Stat label="Status" value={pollStatus.configured ? "Active" : (pollStatus.poll_enabled ? "Needs inbox" : "Paused")} tone={pollStatus.configured ? "text-rag-green-fg" : "text-rag-amber-fg"} />
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Poll interval (sec)</div>
                <div className="flex items-center gap-1 mt-0.5">
                  <Input data-testid="soc-poll-interval-input" type="number" min="15" value={pollStatus.interval_seconds} onChange={e => setPollStatus({ ...pollStatus, interval_seconds: e.target.value })} className="rounded-sm h-7 w-20 text-xs font-mono px-2" />
                  <Button data-testid="soc-poll-interval-save" onClick={saveInterval} variant="outline" size="sm" className="rounded-sm h-7 font-mono text-[10px] uppercase px-2">Save</Button>
                </div>
              </div>
              <Stat label="Processed folder" value={pollStatus.processed_folder || "—"} />
              <Stat label="Cases from email" value={pollStatus.cases_from_email ?? 0} />
            </div>
          )}
          <p className="text-[10px] text-muted-foreground font-mono">Polling runs server-side while enabled and requires Graph Mail access on the SOC mailbox. "Poll now" pulls immediately regardless of the toggle.</p>
        </Card>
      )}

      <Button data-testid="soc-settings-save" onClick={save} disabled={saving} className="rounded-sm font-mono uppercase text-xs">{saving ? "Saving…" : "Save settings"}</Button>

      {alerts && (
        <Card className="p-5 border-border rounded-sm space-y-4" data-testid="soc-alerts-card">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Alerting</div>
            <p className="text-xs text-muted-foreground mt-1">Dedicated SOC Microsoft Teams webhook + email (to case assignee + SOC leads) for Critical cases and SLA breaches.</p>
          </div>
          <div>
            <label className="text-xs font-mono text-muted-foreground">SOC Teams webhook URL {alerts.webhook_set && <span className="text-rag-green-fg">· configured ({alerts.webhook_url})</span>}</label>
            <Input data-testid="soc-webhook-input" value={webhook} onChange={e => setWebhook(e.target.value)} placeholder={alerts.webhook_set ? "Enter new URL to replace…" : "https://outlook.office.com/webhook/…"} className="rounded-sm mt-1 text-sm font-mono" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Toggle label="Teams alerts" testid="soc-toggle-teams" checked={alerts.teams_enabled} onChange={v => setAlerts({ ...alerts, teams_enabled: v })} />
            <Toggle label="Email alerts" testid="soc-toggle-email" checked={alerts.email_enabled} onChange={v => setAlerts({ ...alerts, email_enabled: v })} />
            <Toggle label="On Critical case" testid="soc-toggle-critical" checked={alerts.on_critical} onChange={v => setAlerts({ ...alerts, on_critical: v })} />
            <Toggle label="On SLA breach" testid="soc-toggle-sla" checked={alerts.on_sla_breach} onChange={v => setAlerts({ ...alerts, on_sla_breach: v })} />
            <Toggle label="On ingest stalled" testid="soc-toggle-ingest" checked={alerts.on_ingest_stale} onChange={v => setAlerts({ ...alerts, on_ingest_stale: v })} />
            <label className="flex items-center justify-between gap-2 text-sm border border-border rounded-sm px-3 py-2">
              <span>Ingest stale after (hrs)</span>
              <Input data-testid="soc-ingest-hours" type="number" min="1" value={alerts.ingest_stale_hours ?? 6} onChange={e => setAlerts({ ...alerts, ingest_stale_hours: parseInt(e.target.value || "6", 10) })} className="rounded-sm h-7 w-16 text-xs text-right" />
            </label>
            <Toggle label="On case untaken (unassigned)" testid="soc-toggle-unassigned" checked={alerts.on_unassigned ?? true} onChange={v => setAlerts({ ...alerts, on_unassigned: v })} />
            <label className="flex items-center justify-between gap-2 text-sm border border-border rounded-sm px-3 py-2">
              <span>Untaken alert after (min)</span>
              <Input data-testid="soc-unassigned-minutes" type="number" min="1" value={alerts.unassigned_alert_minutes ?? 30} onChange={e => setAlerts({ ...alerts, unassigned_alert_minutes: parseInt(e.target.value || "30", 10) })} className="rounded-sm h-7 w-16 text-xs text-right" />
            </label>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="soc-alerts-save" onClick={saveAlerts} disabled={savingAlerts} className="rounded-sm font-mono uppercase text-xs">{savingAlerts ? "Saving…" : "Save alert settings"}</Button>
            <Button data-testid="soc-alerts-test" onClick={testAlert} variant="outline" className="rounded-sm font-mono uppercase text-xs">Send test</Button>
          </div>
        </Card>
      )}

      <Card className="p-5 border-border rounded-sm" data-testid="soc-teams-card">
        <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-3">SOC Teams & Roster</div>
        <p className="text-xs text-muted-foreground mb-3">Native SOC teams (e.g. SOC Tier 1, Threat Hunting, IR Team) and their member analysts. Membership powers the "My Team" case/dashboard filter; it is separate from per-case team assignment.</p>
        <div className="space-y-3 mb-4">
          {roster.map(t => {
            const memberIds = new Set((t.members || []).map(m => m.id));
            const candidates = analysts.filter(a => !memberIds.has(a.id));
            return (
              <div key={t.id} data-testid={`soc-roster-team-${t.id}`} className="border border-border rounded-sm p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-medium">{t.name}</span>
                  <span className="text-[10px] font-mono text-muted-foreground">{(t.members || []).length} member(s)</span>
                  <button data-testid={`soc-team-del-${t.id}`} onClick={() => delTeam(t.id)} className="ml-auto text-muted-foreground hover:text-rag-red" title="Delete team"><X size={14} /></button>
                </div>
                <div className="flex flex-wrap gap-2 mb-2">
                  {(t.members || []).map(m => (
                    <span key={m.id} data-testid={`soc-roster-member-${t.id}-${m.id}`} className={`flex items-center gap-1.5 text-xs font-mono px-2 py-1 border rounded-sm ${m.is_team_lead ? "border-primary/50 bg-primary/10" : "border-border bg-accent/30"}`}>
                      {m.is_team_lead && <Star size={11} weight="fill" className="text-primary" data-testid={`soc-roster-lead-badge-${t.id}-${m.id}`} title="Team Lead" />}
                      {m.name}<span className="text-[9px] uppercase text-muted-foreground">{m.role === "soc_lead" ? "lead" : m.role === "soc_auditor" ? "auditor" : `t${m.soc_tier || 1}`}</span>
                      {m.is_team_lead
                        ? <span className="text-[9px] uppercase tracking-wider text-primary font-bold">Team Lead</span>
                        : <button data-testid={`soc-roster-makelead-${t.id}-${m.id}`} onClick={() => makeLead(t.id, m.id)} className="text-muted-foreground hover:text-primary" title="Make team lead"><Star size={11} /></button>}
                      <button data-testid={`soc-roster-remove-${t.id}-${m.id}`} onClick={() => removeMember(t.id, m.id)} className="text-muted-foreground hover:text-rag-red"><X size={11} /></button>
                    </span>
                  ))}
                  {(t.members || []).length === 0 && <span className="text-xs text-muted-foreground">No members yet.</span>}
                </div>
                <div className="flex items-center gap-2">
                  <Select value={memberPick[t.id] || "none"} onValueChange={v => setMemberPick(m => ({ ...m, [t.id]: v }))}>
                    <SelectTrigger data-testid={`soc-roster-pick-${t.id}`} className="rounded-sm h-8 text-xs max-w-xs"><SelectValue placeholder="Add analyst…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Add analyst…</SelectItem>
                      {candidates.map(a => <SelectItem key={a.id} value={a.id}>{a.name} ({a.email})</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button data-testid={`soc-roster-add-${t.id}`} variant="outline" size="sm" className="rounded-sm text-xs font-mono uppercase h-8" onClick={() => addMember(t.id)}><Plus size={13} className="mr-1" /> Add</Button>
                </div>
              </div>
            );
          })}
          {roster.length === 0 && <span className="text-xs text-muted-foreground">No teams yet.</span>}
        </div>
        <div className="flex items-center gap-2">
          <Input data-testid="soc-team-input" value={newTeam} onChange={e => setNewTeam(e.target.value)} placeholder="Add team…" className="rounded-sm h-9 text-sm max-w-xs"
            onKeyDown={e => { if (e.key === "Enter") addTeam(); }} />
          <Button data-testid="soc-team-add" variant="outline" className="rounded-sm text-xs font-mono uppercase" onClick={addTeam}><Plus size={14} /></Button>
        </div>
      </Card>

      {ingest && (
        <Card className="p-5 border-border rounded-sm space-y-3" data-testid="soc-dedup-card">
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Webhook Duplicate Detection</div>
          <p className="text-xs text-muted-foreground">Flags newly ingested cases as a "possible duplicate" when a case with the same source and a similar title arrived within the window. Never auto-merges or blocks.</p>
          <Toggle label="Fuzzy duplicate detection" testid="soc-toggle-dedup" checked={ingest.fuzzy_dedup} onChange={v => saveDedup({ fuzzy_dedup: v })} />
          <label className="flex items-center justify-between gap-2 text-sm border border-border rounded-sm px-3 py-2">
            <span>Detection window (minutes)</span>
            <Input data-testid="soc-dedup-window" type="number" min="1" value={ingest.dedup_window_minutes ?? 15}
              onChange={e => setIngest({ ...ingest, dedup_window_minutes: parseInt(e.target.value || "15", 10) })}
              onBlur={e => saveDedup({ dedup_window_minutes: parseInt(e.target.value || "15", 10) })}
              className="rounded-sm h-7 w-20 text-xs text-right" />
          </label>
        </Card>
      )}

      <SocPhase3Settings />

      <ThreatIntelFeedsCard />
      <NotifyConfigCard />
      <OutboundWebhooksCard />
      <SocResetCard />
        </TabsContent>
        <TabsContent value="business-hours" className="mt-5">
          <SocBusinessHours clients={socClients} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function MitreGateCard() {
  const [gate, setGate] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/soc/settings").then(r => setGate(r.data.mitre_gate || { enabled: true, min_reason_len: 10 })).catch(() => {}); }, []);
  const save = async (patch) => {
    const next = { ...gate, ...patch };
    setSaving(true);
    try { const { data } = await api.put("/soc/settings/mitre-gate", next); setGate(data); toast.success(data.enabled ? "MITRE closure gate enabled" : "MITRE closure gate disabled"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  if (!gate) return null;
  return (
    <Card className="p-5 border-border rounded-sm" data-testid="soc-mitre-gate-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-1">MITRE ATT&CK closure gate</div>
          <p className="text-xs text-muted-foreground">True Positive cases must carry a technique, a tactic, or a "not mappable" reason before they can be Resolved/Closed. Leads/admins may override with a logged reason. Enforced server-side.</p>
        </div>
        <Switch data-testid="soc-mitre-gate-enabled" checked={!!gate.enabled} disabled={saving} onCheckedChange={v => save({ enabled: v })} />
      </div>
      <div className="flex items-center gap-3 mt-4">
        <span className="text-xs font-mono uppercase text-muted-foreground">Minimum reason length</span>
        <Input data-testid="soc-mitre-gate-minlen" type="number" min={1} max={500} value={gate.min_reason_len} onChange={e => setGate({ ...gate, min_reason_len: Number(e.target.value) })} onBlur={() => save({})} className="rounded-sm h-8 w-24 text-sm" />
        <span className="text-[11px] text-muted-foreground">characters (applies to not-mappable and override reasons)</span>
      </div>
    </Card>
  );
}

function SocResetCard() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);
  const isAdmin = user?.role === "admin";
  const loadLog = () => api.get("/soc/reset/log").then(r => setLog(r.data)).catch(() => {});
  useEffect(() => { if (isAdmin) loadLog(); }, [isAdmin]);
  if (!isAdmin) return null;  // Admin only
  const doReset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/soc/reset", { confirm });
      toast.success(`SOC reset complete — ${data.total} record(s) removed. Case numbering restarts at SOC-00001.`);
      setOpen(false); setConfirm(""); loadLog();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); }
    finally { setBusy(false); }
  };
  const fmt = (iso) => fmtIST(iso, { seconds: true });
  return (
    <Card className="p-5 border-rag-red/40 rounded-sm space-y-3" data-testid="soc-reset-card">
      <div className="text-[11px] font-mono uppercase tracking-widest text-rag-red flex items-center gap-2"><Warning size={14} /> Danger Zone</div>
      <p className="text-xs text-muted-foreground">
        Permanently delete <b>all SOC cases</b> and everything attached to them — tasks, observables/IOCs,
        comments, timeline, evidence, playbook checklists, notifications and ITSM links — and restart case
        numbering at SOC-00001. Your configuration is kept: teams &amp; roster, task templates, playbooks,
        triage rules, SLA, threat-intel feeds and the Threat Intel Library. This cannot be undone. Once reset,
        the demo sample cases will <b>not</b> reappear after a restart.
      </p>
      <Button data-testid="soc-reset-open" variant="outline" onClick={() => { setConfirm(""); setOpen(true); }}
        className="rounded-sm font-mono uppercase text-xs border-rag-red/50 text-rag-red-fg hover:bg-rag-red-bg">
        <Trash size={14} className="mr-1" /> Reset SOC (delete all cases)
      </Button>

      {log.length > 0 && (
        <div className="pt-2 border-t border-border" data-testid="soc-reset-log">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Reset history</div>
          <div className="space-y-1">
            {log.map(r => (
              <div key={r.id} data-testid={`soc-reset-log-${r.id}`} className="text-xs flex flex-wrap items-center gap-x-2 text-muted-foreground">
                <span className="font-mono text-foreground">{fmt(r.at)}</span>
                <span>·</span>
                <span className="font-medium text-foreground">{r.by_name || "Unknown"}</span>
                {r.by_email && <span className="font-mono">({r.by_email})</span>}
                <span>·</span>
                <span>{r.total} record(s) removed</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-md" data-testid="soc-reset-dialog">
          <DialogHeader><DialogTitle className="font-mono uppercase text-sm flex items-center gap-2 text-rag-red-fg"><Warning size={16} /> Reset SOC module</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">This deletes every SOC case and all attached data, and cannot be undone. Configuration (teams, templates, playbooks, rules, feeds, library) is preserved.</p>
            <div>
              <label className="text-xs font-mono text-muted-foreground">Type <b className="text-foreground">RESET</b> to confirm</label>
              <Input data-testid="soc-reset-confirm-input" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="RESET"
                className="rounded-sm mt-1 font-mono" onKeyDown={e => { if (e.key === "Enter" && confirm === "RESET") doReset(); }} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm text-xs font-mono uppercase" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="soc-reset-confirm" disabled={confirm !== "RESET" || busy}
              className="rounded-sm text-xs font-mono uppercase bg-rag-red hover:bg-rag-red text-background" onClick={doReset}>
              {busy ? "Resetting…" : "Delete all cases"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

const Toggle = ({ label, testid, checked, onChange }) => (
  <label className="flex items-center justify-between gap-2 text-sm border border-border rounded-sm px-3 py-2 cursor-pointer">
    <span>{label}</span>
    <Switch data-testid={testid} checked={!!checked} onCheckedChange={onChange} />
  </label>
);

const FEED_STATUS_TONE = { ok: "text-rag-green-fg", error: "text-rag-red-fg" };

function ThreatIntelFeedsCard() {
  const [feeds, setFeeds] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/soc/feeds").then(r => setFeeds(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/soc/feeds/templates").then(r => setTemplates(r.data)).catch(() => {});
  }, []);

  const startAdd = (tpl) => setForm({
    name: tpl.name, connector: tpl.connector, feed_type: tpl.feed_type, url: tpl.url,
    auth_key: "", poll_interval_minutes: tpl.default_interval || 360, enabled: true,
    requires_key: tpl.requires_key, help: tpl.help,
  });

  const createFeed = async () => {
    setAdding(true);
    try {
      await api.post("/soc/feeds", {
        name: form.name, connector: form.connector, feed_type: form.feed_type, url: form.url,
        auth_key: form.auth_key, poll_interval_minutes: parseInt(form.poll_interval_minutes, 10), enabled: form.enabled,
      });
      toast.success("Feed added"); setForm(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add feed"); }
    finally { setAdding(false); }
  };

  const patchFeed = async (id, patch) => {
    try { await api.patch(`/soc/feeds/${id}`, patch); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const pollNow = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post(`/soc/feeds/${id}/poll`);
      const r = data.result;
      if (r.status === "ok") toast.success(`Pulled ${r.count} indicator(s), ${r.new} new`);
      else toast.error(`Poll ${r.status}: ${r.detail || ""}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Poll failed"); }
    finally { setBusy(null); }
  };

  const removeFeed = async (id) => {
    if (!window.confirm("Delete this feed source?")) return;
    try { await api.delete(`/soc/feeds/${id}`); load(); } catch { /* */ }
  };

  return (
    <Card className="p-5 border-border rounded-sm space-y-4" data-testid="soc-feeds-card">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2"><Rss size={14} /> Threat Intel Feeds</div>
        <p className="text-xs text-muted-foreground mt-1">Scheduled bulk feeds populate the standalone Threat Intel Library (separate from per-observable enrichment). Feed data never touches cases automatically — it surfaces during investigation.</p>
      </div>

      {feeds.length === 0 && <p className="text-xs text-muted-foreground">No feeds configured yet. Add one below.</p>}

      <div className="space-y-3">
        {feeds.map(f => (
          <div key={f.id} data-testid={`soc-feed-${f.id}`} className="border border-border rounded-sm p-3 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-sm">{f.name}</span>
              <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 border border-border rounded-sm">{f.connector}</span>
              {f.requires_key && (
                <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded-sm ${f.key_set ? "text-rag-green-fg" : "text-rag-amber-fg"}`}>
                  {f.key_set ? `key ${f.key_hint}` : "key required"}
                </span>
              )}
              <div className="ml-auto flex items-center gap-2">
                <Switch data-testid={`soc-feed-enabled-${f.id}`} checked={f.enabled} onCheckedChange={v => patchFeed(f.id, { enabled: v })} />
                <button data-testid={`soc-feed-poll-${f.id}`} onClick={() => pollNow(f.id)} disabled={busy === f.id}
                  className="text-xs font-mono uppercase text-primary hover:underline flex items-center gap-1 disabled:opacity-50">
                  <ArrowClockwise size={13} className={busy === f.id ? "animate-spin" : ""} /> {busy === f.id ? "Polling…" : "Poll now"}
                </button>
                <button data-testid={`soc-feed-del-${f.id}`} onClick={() => removeFeed(f.id)} className="text-muted-foreground hover:text-rag-red"><Trash size={14} /></button>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] font-mono text-muted-foreground">
              <span className="flex items-center gap-1">Every
                <Input data-testid={`soc-feed-interval-${f.id}`} type="number" min="15" defaultValue={f.poll_interval_minutes}
                  onBlur={e => { const v = parseInt(e.target.value, 10); if (v && v !== f.poll_interval_minutes) patchFeed(f.id, { poll_interval_minutes: v }); }}
                  className="rounded-sm h-6 w-16 text-xs text-right inline-block" /> min
              </span>
              <span>Last: {f.last_pull_status
                ? <span className={FEED_STATUS_TONE[f.last_pull_status] || ""}>{f.last_pull_status}{f.last_pull_count != null ? ` · ${f.last_pull_count} pulled` : ""}{f.last_pull_detail ? ` · ${f.last_pull_detail}` : ""}</span>
                : "never polled"}</span>
            </div>
            {f.requires_key && (
              <Input data-testid={`soc-feed-key-${f.id}`} type="password" placeholder={f.key_set ? "Enter new key to replace…" : "Paste auth key"}
                onKeyDown={e => { if (e.key === "Enter" && e.target.value.trim()) { patchFeed(f.id, { auth_key: e.target.value.trim() }); e.target.value = ""; toast.success("Key saved"); } }}
                className="rounded-sm h-7 text-xs font-mono" />
            )}
          </div>
        ))}
      </div>

      {form ? (
        <div className="border border-primary/40 rounded-sm p-3 space-y-2" data-testid="soc-feed-add-form">
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">New feed · {form.connector}</div>
          <Input data-testid="soc-feed-form-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Feed name" className="rounded-sm h-8 text-sm" />
          <Input data-testid="soc-feed-form-url" value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} placeholder="Feed URL" className="rounded-sm h-8 text-sm font-mono" />
          {form.requires_key && (
            <>
              <Input data-testid="soc-feed-form-key" type="password" value={form.auth_key} onChange={e => setForm({ ...form, auth_key: e.target.value })} placeholder="Auth key" className="rounded-sm h-8 text-sm font-mono" />
              {form.help && <p className="text-[11px] text-muted-foreground">{form.help}</p>}
            </>
          )}
          <div className="flex items-center gap-2 text-[11px] font-mono text-muted-foreground">
            Poll every
            <Input data-testid="soc-feed-form-interval" type="number" min="15" value={form.poll_interval_minutes} onChange={e => setForm({ ...form, poll_interval_minutes: e.target.value })} className="rounded-sm h-7 w-16 text-xs text-right" /> min
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="soc-feed-form-save" onClick={createFeed} disabled={adding} className="rounded-sm font-mono uppercase text-xs">{adding ? "Adding…" : "Add feed"}</Button>
            <Button variant="outline" onClick={() => setForm(null)} className="rounded-sm font-mono uppercase text-xs">Cancel</Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2 pt-1">
          {templates.map(t => (
            <Button key={t.connector} data-testid={`soc-feed-template-${t.connector}`} variant="outline" size="sm"
              onClick={() => startAdd(t)} className="rounded-sm text-xs font-mono">
              <Plus size={12} /> {t.name}
            </Button>
          ))}
        </div>
      )}
    </Card>
  );
}

const Stat = ({ label, value, tone = "" }) => (
  <div>
    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className={`text-sm font-mono mt-0.5 truncate ${tone}`}>{value}</div>
  </div>
);

const EVENTS = ["created", "updated", "closed"];

function NotifyConfigCard() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/soc/notify/config").then(r => setCfg(r.data)).catch(() => {}); }, []);
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/soc/notify/config", cfg); setCfg(data); toast.success("Notification settings saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  if (!cfg) return null;
  return (
    <Card className="p-5 border-border rounded-sm space-y-4" data-testid="soc-notify-config-card">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Notifications & Mentions</div>
        <p className="text-xs text-muted-foreground mt-1">Channel push for @mentions and the daily digest schedule. Individual analysts choose their own delivery under the SOC top bar (sliders icon).</p>
      </div>
      <Toggle label="Push every @mention to the SOC Teams channel" testid="soc-toggle-mention-push"
        checked={cfg.mention_channel_push} onChange={v => setCfg({ ...cfg, mention_channel_push: v })} />
      <label className="flex items-center justify-between gap-2 text-sm border border-border rounded-sm px-3 py-2">
        <span>Daily digest send hour (IST, 0–23)</span>
        <Input data-testid="soc-digest-hour" type="number" min="0" max="23" value={cfg.digest_hour_ist ?? 9}
          onChange={e => setCfg({ ...cfg, digest_hour_ist: parseInt(e.target.value || "9", 10) })}
          className="rounded-sm h-7 w-16 text-xs text-right" />
      </label>
      <Button data-testid="soc-notify-config-save" onClick={save} disabled={saving} className="rounded-sm font-mono uppercase text-xs">{saving ? "Saving…" : "Save"}</Button>
    </Card>
  );
}

function OutboundWebhooksCard() {
  const [cfg, setCfg] = useState(null);
  const [hostsText, setHostsText] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const load = () => api.get("/soc/outbound/config").then(r => { setCfg(r.data); setHostsText((r.data.allowed_hosts || []).join("\n")); }).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    setSaving(true);
    const allowed_hosts = hostsText.split(/[\n,]+/).map(s => s.trim()).filter(Boolean);
    try {
      const { data } = await api.put("/soc/outbound/config", { enabled: cfg.enabled, allowed_hosts, endpoints: cfg.endpoints || [] });
      setCfg(data); setHostsText((data.allowed_hosts || []).join("\n")); toast.success("Outbound webhooks saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/soc/outbound/test");
      const r = (data.recent || [])[0];
      if (!r) { toast.message("No endpoint fired — enable and configure a matching endpoint first"); return; }
      const msg = `Endpoint responded: ${r.detail || r.url} (${r.status})`;
      if (r.status === "sent") toast.success(msg);
      else if (r.status === "blocked") toast.warning(msg);
      else toast.error(msg);
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    finally { setTesting(false); }
  };
  const addEndpoint = () => setCfg({ ...cfg, endpoints: [...(cfg.endpoints || []), { url: "", events: ["created", "updated", "closed"], enabled: true }] });
  const updEndpoint = (i, patch) => setCfg({ ...cfg, endpoints: cfg.endpoints.map((e, idx) => idx === i ? { ...e, ...patch } : e) });
  const delEndpoint = (i) => setCfg({ ...cfg, endpoints: cfg.endpoints.filter((_, idx) => idx !== i) });
  const toggleEvent = (i, ev) => { const e = cfg.endpoints[i]; const has = (e.events || []).includes(ev); updEndpoint(i, { events: has ? e.events.filter(x => x !== ev) : [...(e.events || []), ev] }); };
  if (!cfg) return null;
  return (
    <Card className="p-5 border-border rounded-sm space-y-4" data-testid="soc-outbound-card">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Outbound Webhooks (case events)</div>
        <p className="text-xs text-muted-foreground mt-1">POST a JSON payload to your SIEM/SOAR when a case is created, updated or closed. Destinations must use HTTPS and match a host in the allowlist below; internal/private addresses are always blocked.</p>
      </div>
      <Toggle label="Enable outbound webhooks" testid="soc-outbound-enabled" checked={cfg.enabled} onChange={v => setCfg({ ...cfg, enabled: v })} />
      <div>
        <label className="text-xs font-mono text-muted-foreground">Allowed hostnames (one per line)</label>
        <Textarea data-testid="soc-outbound-hosts" value={hostsText} onChange={e => setHostsText(e.target.value)}
          placeholder={"hooks.mysiem.com\nautomation.example.com"} className="rounded-sm mt-1 text-sm font-mono min-h-[70px]" />
      </div>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Endpoints</div>
          <Button data-testid="soc-outbound-add" variant="outline" size="sm" className="rounded-sm text-xs font-mono uppercase" onClick={addEndpoint}><Plus size={13} /> Add</Button>
        </div>
        {(cfg.endpoints || []).length === 0 && <p className="text-xs text-muted-foreground">No endpoints configured.</p>}
        {(cfg.endpoints || []).map((ep, i) => (
          <div key={i} data-testid={`soc-outbound-ep-${i}`} className="border border-border rounded-sm p-3 space-y-2">
            <div className="flex items-center gap-2">
              <Input data-testid={`soc-outbound-url-${i}`} value={ep.url} onChange={e => updEndpoint(i, { url: e.target.value })}
                placeholder="https://hooks.mysiem.com/soc" className="rounded-sm h-8 text-sm font-mono flex-1" />
              <Switch data-testid={`soc-outbound-ep-enabled-${i}`} checked={ep.enabled !== false} onCheckedChange={v => updEndpoint(i, { enabled: v })} />
              <button data-testid={`soc-outbound-del-${i}`} onClick={() => delEndpoint(i)} className="text-muted-foreground hover:text-rag-red"><X size={15} /></button>
            </div>
            <div className="flex flex-wrap gap-3">
              {EVENTS.map(ev => (
                <label key={ev} className="flex items-center gap-1.5 text-xs font-mono">
                  <input type="checkbox" data-testid={`soc-outbound-ev-${i}-${ev}`} checked={(ep.events || []).includes(ev)} onChange={() => toggleEvent(i, ev)} />
                  {ev}
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <Button data-testid="soc-outbound-save" onClick={save} disabled={saving} className="rounded-sm font-mono uppercase text-xs">{saving ? "Saving…" : "Save webhooks"}</Button>
        <Button data-testid="soc-outbound-test" onClick={test} disabled={testing} variant="outline" className="rounded-sm font-mono uppercase text-xs">{testing ? "Testing…" : "Send test"}</Button>
      </div>
    </Card>
  );
}
